import React, { useState, useRef, useEffect } from "react";
import { connect } from "react-redux";
import { Icon } from "semantic-ui-react";
import { withTranslation } from "react-i18next";
import { addDays } from "date-fns";
import { DateTime } from "luxon";

import RescheduleExamModal from "./RescheduleExamModal";
import ConfirmationModal from "./ConfirmationModal";
import {
  getBookingByBookingId,
  adminCancel,
  deleteExam,
} from "../lib/bookingServices";
import parseTimezone from "../lib/helperServices";
import { sendEmail } from "../lib/emailService";
import BookingCancelled from "./emails/BookingCancelled";
import i18n from "../i18n";

import DataTable, {
  SimpleDropdownFilter,
  AsyncDropdownFilter,
} from "./Table/DataTable";
import { DropdownRenderer } from "./Table/CustomComponents";

function StatusRenderer(params) {
  const status = params.value.status;
  const color = ["approved", "completed"].includes(status) ? "green" : "red";
  return (
    <React.Fragment>
      <span style={{ color: status === "pending" ? null : color }}>
        {params.text}
      </span>
      {params.value.file_upload_required ? (
        <a href="#" style={{ color: "black" }} onClick={params.value.onClick}>
          <Icon name="file outline" size="large" />
        </a>
      ) : null}
    </React.Fragment>
  );
}

const textFilterParams = {
  filterOptions: ["contains"],
  trimInput: true,
  debounceMs: 1000,
};

function getFilterParams(filterValues) {
  const params = {
    username: filterValues.username?.filter,
    firstName: filterValues.first_name?.filter,
    lastName: filterValues.last_name?.filter,
    status: filterValues.approved,
    from: filterValues.booking_date?.dateFrom,
    to: filterValues.booking_date?.dateTo,
    exam: filterValues.exam,
  };

  if (filterValues.booking_date) {
    // Server expects date in 'to' with 'from' null for filtering for dates before
    if (filterValues.booking_date.type === "lessThan") {
      params.to = params.from;
      params.from = null;
    } else if (filterValues.booking_date.type === "equals") {
      // If searching for specific date, just do a ranged filter which is exclusive of 'to' date
      params.to = addDays(new Date(params.from), 1);
    }
  }

  return params;
}

const RescheduleExamTable = (props) => {
  const timezone = parseTimezone(
    props.kc.tokenParsed.timezone,
    props.kc.tokenParsed.province
  );

  const [selectedId, setSelectedId] = useState(null);
  const [cancelId, setCancelId] = useState(null);
  const [booking, setBooking] = useState(null);
  const [openRescheduleModal, setOpenRescheduleModal] = useState(false);
  const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
  const [openConfirmCancelModal, setOpenConfirmCancelModal] = useState(false);
  const [cancelWithRefund, setCancelWithRefund] = useState(false);
  const tableRef = useRef();

  useEffect(() => {
    if (selectedId) {
      getBookingByBookingId(selectedId).then((booking) => setBooking(booking));
    }
  }, [selectedId]);

  async function submitDelete() {
    await deleteExam(booking.id);
    setOpenConfirmDeleteModal(false);
    tableRef.current.refresh();
  }

  async function submitCancel() {
    const response = await adminCancel(cancelId, cancelWithRefund);

    // Mirrors the self-service cancel email in ScheduledExams.js - a failure
    // here shouldn't stop the modal from closing or the table from refreshing.
    try {
      const userData = response.userData;
      const wasActuallyRefunded = response.refunded || false;
      const wasCharged = response.wasCharged ?? true;
      const cancellationStatus = wasActuallyRefunded
        ? "refunded"
        : wasCharged
        ? "noRefund"
        : "notCharged";

      const locale = userData.attributes?.locale?.[0] || "en";
      const emailT = i18n.getFixedT(locale);
      const rawTimezone = userData.attributes?.timezone?.[0] || "";
      const timezoneAbbr = rawTimezone.length > 0 ? rawTimezone[0] + "T" : "ET";
      const studentTimezone = parseTimezone(
        rawTimezone,
        userData.attributes?.province?.[0]
      );
      const formattedDate = DateTime.fromISO(response.bookingDate)
        .setZone(studentTimezone)
        .toFormat("MMMM dd, yyyy - hh:mm a");

      const subject = wasActuallyRefunded
        ? emailT("Emails.BookingCancelled.subjectWithRefund")
        : emailT("Emails.BookingCancelled.subjectNoRefund");

      await sendEmail({
        to: userData.email,
        subject,
        component: (
          <BookingCancelled
            t={emailT}
            name={`${userData.firstName} ${userData.lastName}`}
            examTitle={response.examTitle}
            bookingDate={formattedDate}
            timezone={timezoneAbbr}
            duration={response.duration}
            status={cancellationStatus}
            refundAmount={response.refundAmount}
          />
        ),
      });
    } catch (emailError) {
      console.error("[RescheduleExamTable] Failed to send cancellation email:", emailError);
    }

    setOpenConfirmCancelModal(false);
    setCancelId(null);
    setCancelWithRefund(false);
    tableRef.current.refresh();
  }

  async function updateBooking(details) {
    await props.handleUpdateBooking(details);
    setOpenRescheduleModal(false);
    tableRef.current.refresh();
  }
  return (
    <React.Fragment>
      {booking ? (
        <RescheduleExamModal
          open={openRescheduleModal}
          booking={booking}
          updateBooking={updateBooking}
          close={() => setOpenRescheduleModal(false)}
        />
      ) : null}

      <ConfirmationModal
        open={openConfirmDeleteModal}
        close={() => setOpenConfirmDeleteModal(false)}
        submit={submitDelete}
        headerText={props.t("ExamTable.deleteExam")}
        confirmationMsg={props.t("ExamTable.confirmDelete")}
      />
      <ConfirmationModal
        open={openConfirmCancelModal}
        close={() => {
          setOpenConfirmCancelModal(false);
          setCancelId(null);
          setCancelWithRefund(false);
        }}
        submit={submitCancel}
        headerText={props.t("RescheduleExam.cancelExam")}
        confirmationMsg={
          cancelWithRefund
            ? props.t(
                "RescheduleExam.cancelExamWithRefundMsg",
                "Are you sure you want to cancel this booking? The user will receive a full refund."
              )
            : props.t(
                "RescheduleExam.cancelExamNoRefundMsg",
                "Are you sure you want to cancel this booking? No refund will be issued."
              )
        }
      />
      <DataTable
        ref={tableRef}
        columnDefs={[
          {
            headerName: props.t("general.username"),
            field: "username",
            valueGetter: (row) => row.data?.user.username,
            filter: "agTextColumnFilter",
            filterParams: textFilterParams,
            flex: 2,
          },
          {
            headerName: props.t("general.firstName"),
            field: "first_name",
            valueGetter: (row) => row.data?.user.first_name,
            filter: "agTextColumnFilter",
            filterParams: textFilterParams,
            flex: 1,
          },
          {
            headerName: props.t("general.lastName"),
            field: "last_name",
            valueGetter: (row) => row.data?.user.last_name,
            filter: "agTextColumnFilter",
            filterParams: textFilterParams,
            flex: 1,
          },

          {
            headerName: props.t("RescheduleExam.dateAndTimeSlot"),
            field: "booking_date",
            filter: "agDateColumnFilter",
            filterParams: {
              filterOptions: ["equals", "lessThan", "greaterThan", "inRange"],
              debounceMs: 1000,
              maxNumConditions: 1,
            },
            valueGetter: (row) => {
              if (row.data) {
                const convertedTzDate = DateTime.fromISO(
                  row.data.booking_date
                ).setZone(timezone);
                return convertedTzDate.toFormat("MMMM dd, yyyy - hh:mm a");
              }
              return null;
            },
            flex: 2,
          },
          {
            headerName: props.t("general.examName"),
            field: "exam",
            valueGetter: (row) => row.data?.exam.title,
            filter: AsyncDropdownFilter,
            filterParams: {
              url: `${process.env.REACT_APP_API_ADDRESS}/api/examinations`,
              text: "Exam",
              keyValue: "id",
              keyText: "title",
            },
            flex: 2,
          },
          {
            headerName: props.t("general.status"),
            field: "approved",
            cellRenderer: StatusRenderer,
            cellRendererParams: (row) => ({
              text: row.data ? props.t(`general.${row.data.approved}`) : "",
            }),
            valueGetter: (row) => ({
              status: row.data?.approved,
              onClick: (e) => {
                e.preventDefault();
                handleDownloadUpload(row.data?.id);
              },
              file_upload_required: row.data?.exam.file_upload_required,
            }),
            filter: SimpleDropdownFilter,
            filterParams: {
              options: [
                { value: "approved", text: props.t("general.approved") },
                { value: "rejected", text: props.t("general.rejected") },
                { value: "pending", text: props.t("general.pending") },
                { value: "completed", text: props.t("general.completed") },
                { value: "cancelled", text: props.t("general.cancelled") },
              ],
              text: "Status",
              height: "210px",
            },
            flex: 2,
          },
          {
            headerName: props.t("RescheduleExam.refunded"),
            field: "refunded",
            valueGetter: (row) => {
              if (row.data && row.data.approved === "cancelled") {
                if (row.data.refunded) {
                  return props.t("RescheduleExam.refundedYes");
                }
                // A booking that was still pending/never captured when
                // cancelled has nothing to refund - distinguish that from an
                // actual charged booking that wasn't refunded, same as the
                // cancellation email's refunded/noRefund/notCharged status.
                return row.data.charged
                  ? props.t("RescheduleExam.refundedNo")
                  : props.t("Emails.BookingCancelled.noChargeMade");
              }
              return "-";
            },
            filter: SimpleDropdownFilter,
            filterParams: {
              options: [
                { value: "true", text: props.t("RescheduleExam.refundedYes") },
                { value: "false", text: props.t("RescheduleExam.refundedNo") },
              ],
              text: "Refunded",
              height: "120px",
            },
            flex: 1,
          },
          {
            headerName: props.t("general.actions"),
            cellRenderer: DropdownRenderer,
            cellRendererParams: (row) => {
              return {
                text: props.t("general.edit"),
                row: row
              };
            },
            valueGetter: (row) => {
              if (row.data) {
                return [
                  {
                    text: props.t("RescheduleExam.reschedule"),
                    onClick: () => {
                      setSelectedId(row.data.id);
                      setOpenRescheduleModal(true);
                    },
                  },
                  {
                    text: props.t("general.delete"),
                    onClick: () => {
                      setSelectedId(row.data.id);
                      setOpenConfirmDeleteModal(true);
                    },
                  },
                  {
                    text: props.t("RescheduleExam.cancelWithRefund", "Cancel (With Refund)"),
                    onClick: () => {
                      setCancelId(row.data.id);
                      setCancelWithRefund(true);
                      setOpenConfirmCancelModal(true);
                    },
                  },
                  {
                    text: props.t("RescheduleExam.cancelNoRefund", "Cancel (No Refund)"),
                    onClick: () => {
                      setCancelId(row.data.id);
                      setCancelWithRefund(false);
                      setOpenConfirmCancelModal(true);
                    },
                  },
                ];
              }
            },
            cellStyle: {
              overflow: "inherit",
              textAlign: "center",
              alignSelf: "flex-end",
            },
            flex: 2,
            filter: false,
            sortable: false,
          },
        ]}
        url={`${process.env.REACT_APP_API_ADDRESS}/api/bookings/table`}
        getFilterParams={getFilterParams}
        tableId="reschedule-grid"
      />
    </React.Fragment>
  );
};

export default withTranslation()(
  connect(
    (state) => ({
      kc: state.keycloak,
    }),
    {}
  )(RescheduleExamTable)
);
